import type {ChatMessage} from "@/components/chat/ChatView";
import {isCodeExecutionTool} from "@/components/ask/toolCallUtils";

// Lightweight content part type for grouping logic (avoids importing heavy ChatView deps)
type GroupableContentPart = {type: string; data?: {functionName?: string}; [key: string]: unknown};

export type ContentPartGroup = {type: "thinking" | "text" | "functionCallGroup" | "navigation"; parts: GroupableContentPart[]; startIdx: number};

/** Group consecutive code-execution calls so they share one expandable trace. */
export const groupContentParts = (contentParts: GroupableContentPart[]): ContentPartGroup[] => {
   const groups: ContentPartGroup[] = [];

   for (let i = 0; i < contentParts.length; i++) {
      const part = contentParts[i];
      if (part.type === "functionCall") {
         const funcName = part.data?.functionName ?? "";
         const lastGroup = groups.at(-1);
         if (lastGroup?.type === "functionCallGroup") {
            const lastFuncName = lastGroup.parts[0].data?.functionName ?? "";
            if (isCodeExecutionTool(funcName) && isCodeExecutionTool(lastFuncName)) {
               lastGroup.parts.push(part);
               continue;
            }
         }
         groups.push({type: "functionCallGroup", parts: [part], startIdx: i});
      } else {
         groups.push({type: part.type as "thinking" | "text" | "navigation", parts: [part], startIdx: i});
      }
   }
   return groups;
};

/** Find the index of the last assistant message in the current turn. */
export function findLastAssistantIndex(messages: ChatMessage[], isAiWorking: boolean): number {
   let seenUserAfter = false;
   for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].isUser) {
         seenUserAfter = true;
         continue;
      }
      if (seenUserAfter && !isAiWorking) return -1;
      return i;
   }
   return -1;
}

/** Check if any message has in-progress tool calls. */
export function hasInProgressToolCalls(messages: ChatMessage[]): boolean {
   return messages.some((msg) => {
      if (msg.isUser) return false;
      if (msg.functionCallData && !msg.functionCallCompleted) return true;
      if (msg.contentParts) {
         return msg.contentParts.some(
            (part) => part.type === "functionCall" && !part.isCompleted
         );
      }
      return false;
   });
}

export type TurnSlotMeta = {
   /** Skip the agent header because the previous message in the same turn was also an assistant. */
   hideAgentHeader: boolean;
   /** Index of the message whose text should be copied from this bubble's copy button. Only set when this bubble owns the trailing controls. */
   copySourceIndex?: number;
   /** Index of the message whose id + rating should feed the feedback control. Only set when this bubble owns the trailing controls. */
   feedbackSourceIndex?: number;
   /** True for an assistant text/regular message when a trailing assistant file in the same turn will carry the copy + feedback row. */
   suppressOwnControls: boolean;
};

/**
 * Decide, for each message, how the copy/feedback trailing row and the agent
 * header should be routed for its turn. Extracted so `ChatView` doesn't
 * recompute per render and so the routing rules are unit-testable without
 * mounting the chat.
 *
 * Rules:
 *  - An assistant message hides its header when the previous message is also assistant.
 *  - When an assistant turn ends with a file bubble, that file carries the
 *    copy + feedback row, sourced from the preceding assistant text in the turn.
 *  - The text bubble that would otherwise own those controls suppresses them.
 */
export function computeTurnSlotMeta(messages: ChatMessage[]): TurnSlotMeta[] {
   const meta: TurnSlotMeta[] = messages.map((m, i) => ({
      hideAgentHeader: !m.isUser && i > 0 && !messages[i - 1].isUser,
      suppressOwnControls: false,
   }));

   const isLastAssistantInTurn = (index: number): boolean => {
      const next = messages[index + 1];
      return !next || next.isUser;
   };

   const findPrevAssistantTextInTurn = (index: number): number | undefined => {
      for (let i = index - 1; i >= 0; i--) {
         const m = messages[i];
         if (m.isUser) return undefined;
         if (m.fileData || m.chartData || m.functionCallData || m.scriptDraftData) continue;
         if (m.text?.trim()) return i;
      }
      return undefined;
   };

   for (let i = 0; i < messages.length; i++) {
      const m = messages[i];
      // File and script-draft cards both render as trailing artifact bubbles
      // that carry the turn's copy/feedback row.
      if (m.isUser || !(m.fileData || m.scriptDraftData) || !isLastAssistantInTurn(i)) continue;

      const sourceIdx = findPrevAssistantTextInTurn(i);
      const source = sourceIdx === undefined ? undefined : messages[sourceIdx];

      // Feedback attaches to whichever message has a messageId (prefer the
      // paired text — matches where rating was previously stored).
      if (source?.messageId) {
         meta[i].feedbackSourceIndex = sourceIdx;
      } else if (m.messageId) {
         meta[i].feedbackSourceIndex = i;
      }

      // Copy text comes from the paired assistant text, not the file bubble
      // itself (file bubbles have no meaningful text content).
      if (source?.text?.trim()) {
         meta[i].copySourceIndex = sourceIdx;
      }

      if (sourceIdx !== undefined) {
         meta[sourceIdx].suppressOwnControls = true;
      }
   }

   return meta;
}

/** Check if there's any assistant content after the last user message. */
export function hasAssistantContentAfterLastUser(messages: ChatMessage[]): boolean {
   let lastUserIdx = -1;
   for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].isUser) {
         lastUserIdx = i;
         break;
      }
   }
   return messages.slice(lastUserIdx + 1).some((msg) => {
      if (msg.isUser) return false;
      if (msg.text && msg.text.trim().length > 0) return true;
      if (msg.functionCallData) return true;
      if (msg.scriptDraftData) return true;
      if (msg.contentParts && msg.contentParts.length > 0) return true;
      return false;
   });
}
